import { useState, useCallback, useMemo, useEffect } from "react";
import docentesService from "../../../../services/docentesService";
import mallaService from "../../../../services/mallaService";
import { showToast } from "../../../../utils/showToast";

const POR_PAGINA = 10;

export default function useDocentesLogic() {
  const [docentes, setDocentes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [filtro, setFiltro] = useState("total");
  const [busqueda, setBusqueda] = useState("");
  const [pagina, setPagina] = useState(1);
  const [modal, setModal] = useState("");
  const [docenteSeleccionado, setDocenteSeleccionado] = useState(null);

  const [formNuevo, setFormNuevo] = useState({
    nombres: "",
    apellidos: "",
    documento: "",
    email: "",
    telefono: "",
    activo: true,
  });

  const [formEditar, setFormEditar] = useState({
    nombres: "",
    apellidos: "",
    documento: "",
    email: "",
    telefono: "",
    activo: true,
  });

  const [savingNuevo, setSavingNuevo] = useState(false);
  const [savingEditar, setSavingEditar] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Campus Ademy
  const [modalPin, setModalPin] = useState(null); // { nombre, pin, expira_horas }
  const [togglingCampus, setTogglingCampus] = useState(null); // id del docente en proceso
  const [togglingActivo, setTogglingActivo] = useState(null); // id del docente en proceso

  // Cerrar modal PRIMERO (sin dependencias)
  const cerrarModal = useCallback(() => {
    setModal("");
    setDocenteSeleccionado(null);
  }, []);

  // Cargar docentes
  const fetchDocentes = useCallback(async () => {
    setLoading(true);
    setListError(null);
    try {
      const data = await docentesService.listar();
      setDocentes(data || []);
      setLastUpdated(new Date());
    } catch (error) {
      setListError(error.message || "Error al cargar docentes");
      console.error("Error fetching docentes:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Cargar docentes al montar
  useEffect(() => {
    fetchDocentes();
  }, [fetchDocentes]);

  // Validar docente
  const validarDocente = (doc) => {
    if (!doc.nombres?.trim()) {
      showToast({ type: "danger", message: "El nombre es requerido" });
      return false;
    }
    if (!doc.apellidos?.trim()) {
      showToast({ type: "danger", message: "El apellido es requerido" });
      return false;
    }
    if (!doc.documento?.trim()) {
      showToast({ type: "danger", message: "El documento es requerido" });
      return false;
    }
    return true;
  };

  // Crear docente. materiasSeleccionadas: cursos_materias ids marcados en la
  // pestaña "Materias que dictará" antes de que el docente exista — se aplican
  // recien despues de crearlo, cuando ya tenemos su id.
  const handleCrearDocente = useCallback(
    async (e, materiasSeleccionadas = []) => {
      e.preventDefault();
      if (!validarDocente(formNuevo)) return;

      setSavingNuevo(true);
      try {
        const res = await docentesService.crear(formNuevo);
        const nuevoId = res?.data?.id;

        if (nuevoId && materiasSeleccionadas.length) {
          await Promise.all(
            materiasSeleccionadas.map((cursosMateriasId) =>
              mallaService.agregarDocenteCalificado(cursosMateriasId, nuevoId).catch(() => {})
            )
          );
        }

        showToast({ type: "success", message: "Docente creado correctamente" });
        await fetchDocentes();
        cerrarModal();
        setFormNuevo({
          nombres: "",
          apellidos: "",
          documento: "",
          email: "",
          telefono: "",
          activo: true,
        });
      } catch (error) {
        showToast({ type: "danger", message: error.message || "Error al crear docente" });
      } finally {
        setSavingNuevo(false);
      }
    },
    [formNuevo, fetchDocentes, cerrarModal]
  );

  // Al desactivar un docente, el backend avisa si sigue asignado a materias
  // (preferente o calificado) que no se tocaron ni reasignaron solas.
  const mostrarAvisoDesactivacion = useCallback((aviso) => {
    if (!aviso) return;
    const detalle = aviso.materias
      .map((m) => `${m.materia} (${m.curso})${m.preferente ? " — preferente" : ""}`)
      .join(", ");
    showToast({ type: "warning", message: `${aviso.mensaje} ${detalle}` });
  }, []);

  // Actualizar docente
  const handleActualizarDocente = useCallback(
    async (e) => {
      e.preventDefault();
      if (!validarDocente(formEditar)) return;

      setSavingEditar(true);
      try {
        const res = await docentesService.actualizar(docenteSeleccionado.id, formEditar);
        showToast({ type: "success", message: "Docente actualizado correctamente" });
        mostrarAvisoDesactivacion(res?.aviso);
        await fetchDocentes();
        cerrarModal();
      } catch (error) {
        showToast({ type: "danger", message: error.message || "Error al actualizar docente" });
      } finally {
        setSavingEditar(false);
      }
    },
    [formEditar, docenteSeleccionado, fetchDocentes, cerrarModal, mostrarAvisoDesactivacion]
  );

  // Eliminar docente
  const handleEliminarDocente = useCallback(async () => {
    if (!docenteSeleccionado?.id) return;

    setDeleting(true);
    try {
      await docentesService.eliminar(docenteSeleccionado.id);
      showToast({ type: "success", message: "Docente eliminado correctamente" });
      await fetchDocentes();
      cerrarModal();
    } catch (error) {
      showToast({ type: "danger", message: error.message || "Error al eliminar docente" });
    } finally {
      setDeleting(false);
    }
  }, [docenteSeleccionado, fetchDocentes, cerrarModal]);

  // Abrir modal crear
  const abrirModalNuevo = useCallback(() => {
    setFormNuevo({
      nombres: "",
      apellidos: "",
      documento: "",
      email: "",
      telefono: "",
      activo: true,
    });
    setModal("crear");
  }, []);

  // Abrir modal editar
  const abrirModalEditar = useCallback((docente) => {
    setDocenteSeleccionado(docente);
    setFormEditar({
      nombres: docente.nombres,
      apellidos: docente.apellidos,
      documento: docente.documento,
      email: docente.email || "",
      telefono: docente.telefono || "",
      activo: docente.activo,
    });
    setModal("editar");
  }, []);

  // Abrir modal eliminar
  const abrirModalEliminar = useCallback((docente) => {
    setDocenteSeleccionado(docente);
    setModal("eliminar");
  }, []);

  // Activo/Inactivo: toggle directo desde la fila, sin pasar por el modal de
  // edición. Es el mismo campo que usa Malla/Cronograma para decidir si el
  // docente se puede seguir asignando a materias nuevas.
  // El diálogo de confirmación (DocentesList) ya lista las materias afectadas
  // ANTES de desactivar, así que aquí no se repite ese aviso en un toast que
  // desaparece solo (el backend lo sigue devolviendo en `res.aviso` por si se
  // desactiva desde otro lado, ej. el modal de editar).
  const handleToggleActivo = useCallback(async (docente) => {
    setTogglingActivo(docente.id);
    try {
      await docentesService.actualizar(docente.id, { activo: !docente.activo });
      showToast({
        type: "success",
        message: docente.activo ? "Docente desactivado" : "Docente activado",
      });
      await fetchDocentes();
    } catch (error) {
      showToast({ type: "danger", message: error.message || "No se pudo cambiar el estado del docente" });
    } finally {
      setTogglingActivo(null);
    }
  }, [fetchDocentes]);

  // Campus: toggle habilitar/deshabilitar
  const handleCampusToggle = useCallback(async (docente, habilitar) => {
    setTogglingCampus(docente.id);
    try {
      const res = await docentesService.campusToggle(docente.id, habilitar);
      if (habilitar && res.pin) {
        setModalPin({
          nombre: `${docente.nombres} ${docente.apellidos}`,
          pin: res.pin,
          expira_horas: res.expira_horas,
        });
      } else {
        showToast({ type: "success", message: "Acceso Campus desactivado" });
      }
      await fetchDocentes();
    } catch (error) {
      showToast({ type: "danger", message: error.message || "Error gestionando acceso Campus" });
    } finally {
      setTogglingCampus(null);
    }
  }, [fetchDocentes]);

  // Campus: reset PIN
  const handleCampusReset = useCallback(async (docente) => {
    setTogglingCampus(docente.id);
    try {
      const res = await docentesService.campusReset(docente.id);
      setModalPin({
        nombre: `${docente.nombres} ${docente.apellidos}`,
        pin: res.pin,
        expira_horas: res.expira_horas,
        esReset: true,
      });
    } catch (error) {
      showToast({ type: "danger", message: error.message || "Error regenerando PIN" });
    } finally {
      setTogglingCampus(null);
    }
  }, []);

  // Aplicar filtro
  const aplicarFiltro = useCallback((tipo) => {
    setFiltro(tipo);
  }, []);

  // Calcular resumen
  const resumen = useMemo(() => {
    return {
      total: docentes.length,
      activos: docentes.filter((d) => d.activo).length,
      inactivos: docentes.filter((d) => !d.activo).length,
    };
  }, [docentes]);

  // Resetear página al cambiar filtro o búsqueda
  useEffect(() => { setPagina(1); }, [filtro, busqueda]);

  // Docentes filtrados
  const docentesFiltrados = useMemo(() => {
    let lista = docentes;
    if (filtro === "activos") {
      lista = lista.filter((d) => d.activo);
    }
    if (filtro === "inactivos") {
      lista = lista.filter((d) => !d.activo);
    }
    if (busqueda.trim()) {
      const q = busqueda.trim().toLowerCase();
      lista = lista.filter((d) =>
        (d.nombres || "").toLowerCase().includes(q) ||
        (d.apellidos || "").toLowerCase().includes(q) ||
        (d.documento || "").toLowerCase().includes(q) ||
        (d.email || "").toLowerCase().includes(q)
      );
    }
    return lista;
  }, [docentes, filtro, busqueda]);

  const totalFiltradas = docentesFiltrados.length;
  const totalPaginas   = Math.max(1, Math.ceil(totalFiltradas / POR_PAGINA));

  const docentesPaginados = useMemo(() => {
    const start = (pagina - 1) * POR_PAGINA;
    return docentesFiltrados.slice(start, start + POR_PAGINA);
  }, [docentesFiltrados, pagina]);

  return {
    docentes: docentesPaginados,
    busqueda,
    setBusqueda,
    pagina,
    setPagina,
    totalFiltradas,
    totalPaginas,
    porPagina: POR_PAGINA,
    loading,
    listError,
    lastUpdated,
    resumen,
    filtro,
    modal,
    docenteSeleccionado,
    formNuevo,
    setFormNuevo,
    formEditar,
    setFormEditar,
    savingNuevo,
    savingEditar,
    deleting,
    fetchDocentes,
    abrirModalNuevo,
    abrirModalEditar,
    abrirModalEliminar,
    cerrarModal,
    handleCrearDocente,
    handleActualizarDocente,
    handleEliminarDocente,
    aplicarFiltro,
    // Campus
    modalPin,
    setModalPin,
    togglingCampus,
    handleCampusToggle,
    handleCampusReset,
    togglingActivo,
    handleToggleActivo,
  };
}
